'use client';

import React, { useState, useRef, useEffect } from 'react';
import { Plus } from 'lucide-react';

interface ImageUploadProps {
  onImageSelect: (file: File | null) => void;
  currentImage?: File | null;
  currentImageUrl?: string; // Nueva prop para URL de imagen inicial
}

export const ImageUpload: React.FC<ImageUploadProps> = ({ 
  onImageSelect, 
  currentImage, 
  currentImageUrl 
}) => {
  const [preview, setPreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Efecto para manejar la imagen inicial
  useEffect(() => {
    if (currentImage) {
      // Si hay una nueva imagen seleccionada, mostrarla
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreview(e.target?.result as string);
      };
      reader.readAsDataURL(currentImage);
    } else if (currentImageUrl) {
      // Si hay una URL de imagen inicial, mostrarla
      setPreview(currentImageUrl);
    } else {
      // Si no hay imagen, limpiar el preview
      setPreview(null);
    }
  }, [currentImage, currentImageUrl]);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      onImageSelect(file);
      // El preview se actualizará automáticamente por el useEffect
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleRemoveImage = (e: React.MouseEvent) => {
    e.stopPropagation(); // Evitar que se abra el selector de archivos
    onImageSelect(null);
    setPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div 
      onClick={handleClick}
      className="w-full h-full bg-gray-200 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:bg-gray-300 transition-colors relative overflow-hidden"
    >
      {preview ? (
        <>
          <img 
            src={preview} 
            alt="Event preview" 
            className="w-full h-full object-contain"
          />
          {/* Botón para remover imagen */}
          <button
            onClick={handleRemoveImage}
            className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold transition-colors"
            type="button"
          >
            ×
          </button>
          {/* Overlay para indicar que se puede cambiar */}
          <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-30 transition-all duration-200 flex items-center justify-center">
            <span className="text-white font-medium opacity-0 hover:opacity-100 transition-opacity">
              Click to change image
            </span>
          </div>
        </>
      ) : (
        <>
          <div className="w-12 h-12 bg-gray-400 rounded-lg flex items-center justify-center mb-2">
            <Plus className="w-6 h-6 text-white" />
          </div>
          <span className="text-gray-600 text-sm">Add picture</span>
        </>
      )}
      
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleImageChange}
        className="hidden"
      />
    </div>
  );
};